import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from 'angular2-notifications';
import { AuthService } from 'app/core/services/auth.service';
import { CommonService } from 'app/core/services/common.service';
import { FuseTranslationLoaderService } from 'app/core/services/translation-loader.service';
import { EmailSendingParam } from 'app/main/model/common';
import { RequestCustomer, RequestProject } from 'app/main/model/RequestBase';
import { ResponseBase } from 'app/main/model/ResponseBase';
import { CompanyInfoService } from 'app/main/pages/companyInfo/companyInfo.service';
import { CustomersService } from 'app/main/pages/customer/customer.service';
import { ProjectService } from 'app/main/pages/project/project.service';
import { TextEditorEmailSendComponent } from 'app/main/shared-component/text-editor-email-send/text-editor-email-send.component';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { locale as danish } from './translate/ds';
import { locale as english } from './translate/en';

@Component({
  selector: 'vibe-project-report',
  templateUrl: './project.component.html',
  styleUrls: ['./project.component.scss']
})
export class ProjectReportComponent implements OnInit {
  private _SelectedTab = new BehaviorSubject<number>(null);
  coWokers = [];
  @Input()
  set SelectedTab(value: number) {
    this._SelectedTab.next(value);
  }
  get SelectedTab(): number {
    return this._SelectedTab.getValue();
  }

  @Output() IsTabLoading = new EventEmitter<any>();

  // Dropdown & list data
  allCustomers: any[] = [];
  customers: any[] = [];
  selectedCustomerId: string = null;
  customerSearchText: string = '';

  projects: any[] = [];
  showSpinner = false;

  // Search fields
  startDate: Date = null;
  endDate: Date = null;
  searchAddress: string = '';
  isActive: boolean = true;
  isDeleted: boolean = false;
  startWeek: number = 1;

  // Date helpers for card styling
  todayDate: string = '';
  after5DayDate: string = '';

  constructor(
    private translationLoader: FuseTranslationLoaderService,
    private translate: TranslateService,
    private customersService: CustomersService,
    private datePipe: DatePipe,
    private projectService: ProjectService,
    private authService: AuthService,
    public companyInfoService: CompanyInfoService,
    public dialog: MatDialog,
    private notificationsService: NotificationsService,
    private _router: Router,
    private commonService: CommonService,
  ) {
    this.translationLoader.loadTranslations(english, danish);
  }

  ngOnInit(): void {
    const today = new Date();
    this.endDate = today;
    this.startDate = new Date(today);
    this.startDate.setDate(today.getDate() - 30);

    this.todayDate = moment(today).format('YYYY-MM-DD');
    this.after5DayDate = moment(this.todayDate, 'YYYY-MM-DD').add(5, 'days').format('YYYY-MM-DD');

    // Load customer list for dropdown when component initializes
    this.loadCustomers();

    this._SelectedTab.subscribe(i => {
      if (i === 1) {
        // Tab-specific logic can be added here if needed
      }
    });
  }

  // Load customers for dropdown
  private loadCustomers(): void {
    const req = new RequestCustomer();
    req.IsActive = true;
    req.IsDeleted = false;
    req.Index = 1;
    req.Limit = 20;
    req.OrderBy = 'Name';

    this.customersService.Get<ResponseBase>(req)
      .subscribe((response: ResponseBase) => {
        const list = response && response.result ? response.result : [];
        this.allCustomers = list;
        this.customers = list;
      }, () => {
        this.allCustomers = [];
        this.customers = [];
      });
  }

  // UI handlers
  onCustomerSearchChange(): void {
    const term = (this.customerSearchText || '').toLowerCase();
    if (!term) {
      this.customers = this.allCustomers.slice();
    } else {
      this.customers = this.allCustomers.filter(c =>
        (c.Name && c.Name.toLowerCase().includes(term)) ||
        (c.number && c.number.toString().toLowerCase().includes(term))
      );
    }
  }

  onCustomerSearchButtonClick(): void {
    const termRaw = this.customerSearchText || '';
    const term = termRaw.trim();
    if (!term) {
      // Reset to initial page if search cleared
      this.loadCustomers();
      return;
    }

    // 1) Try to find matches in already loaded list
    const lower = term.toLowerCase();
    const localMatches = this.allCustomers.filter(c =>
      (c.name && c.name.toLowerCase().includes(lower)) ||
      (c.number && c.number.toString().toLowerCase().includes(lower))
    );

    if (localMatches.length > 0) {
      this.customers = localMatches;
      return;
    }

    // 2) If not found locally, call API with the search term (limit 20)
    const req = new RequestCustomer();
    req.IsActive = true;
    req.IsDeleted = false;
    req.Index = 1;
    req.Limit = 20;
    req.Name = term;

    this.customersService.Get<ResponseBase>(req)
      .subscribe(
        (response: ResponseBase) => {
          const list = response && response.result ? response.result : [];
          this.allCustomers = list;
          this.customers = list;
        },
        () => {
          this.allCustomers = [];
          this.customers = [];
        }
      );
  }

  _handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.keyCode === 13) {
      // prevent closing the select panel on Enter
      event.stopPropagation();
      event.preventDefault();
    }
  }

  onCustomerChange(): void {
    this.loadProjects();
  }

  onSearch(): void {
    this.searchAddress = (this.searchAddress || '').trim();
    this.loadProjects();
  }

  onDateRangeChange(): void {
    this.loadProjects();
  }

  onProjectClick(project: any): void {
    // Placeholder for navigation or further handling if needed
  }

  onFocusIn(event: any): void {
    if (event && event.model && typeof event.model.show === 'function') {
      event.model.show();
    }
  }

  onSetActive(active: boolean): void {
    this.isActive = active;
    this.loadProjects();
  }

  onSetDeleted(deleted: boolean): void {
    this.isDeleted = deleted;
    this.loadProjects();
  }

  // Core loader
  private loadProjects(): void {
    const model = new RequestProject();
    if (this.selectedCustomerId) {
      model.CustomerId = this.selectedCustomerId;
    }
    model.Address = this.searchAddress || '';
    model.IsActive = this.isActive;
    model.IsDeleted = this.isDeleted;
    model.Index = 1;
    model.Limit = 100000;
    model.OrderBy = 'Number DESC';

    // Extra search parameters expected by backend
    (model as any).startDate = this.startDate
      ? this.datePipe.transform(this.startDate, 'yyyy-MM-dd')
      : null;

    (model as any).endDate = this.endDate
      ? this.datePipe.transform(this.endDate, 'yyyy-MM-dd')
      : null;
    this.showSpinner = true;

    this.customersService
      .GetProjectsByCustomer<ResponseBase>(this.selectedCustomerId, model)
      .subscribe(
        (response: ResponseBase) => {
          if (response && response.result) {
            this.projects = response.result;
            if (this.projects.length > 0) {
              this.projectService.setBGImageForProjectCards(this.projects);
              this.projects.forEach(p => {
                const formatted = p.startDate ? moment(p.startDate).format('YYYY-MM-DD') : null;
                p.displayStartDateRed = formatted && this.after5DayDate >= formatted;
              });
            }
          } else {
            this.projects = [];
          }
          this.showSpinner = false;
        },
        () => {
          this.projects = [];
          this.showSpinner = false;
        }
      );
  }



  getCoWorkers(model) {
    model.Limit = 400;
    model.OrderBy = "Name";
    this.companyInfoService.GetCoworker<ResponseBase>(model)
      .subscribe((response: ResponseBase) => {
        if (response) {
          //this.coWokers = response.result;
          this.getNextRequestForCoWorker(response.pagination, model, response.result);
        }
        else {
          this.coWokers = [];
          //this.SecurityOfficers = [];
        }
      },
        error => (err) => {
          this.coWokers = [];
          //this.SecurityOfficers = [];
        },
        () => {
        });
  }

  getNextRequestForCoWorker(pagination, model, response) {
    if (pagination.index == 1) {
      this.coWokers = response;
    }
    else {
      this.coWokers.push(...response);
    }
    model.Index = pagination.index + 1;
    model.Limit = pagination.limit;
    model.TotalRows = pagination.totalCount;
    if (pagination.totalCount > pagination.index * pagination.limit)
      this.getCoWorkers(model);
  }

  processFileAndEmail(
    fileType: number,
    emailSending?: EmailSendingParam
  ): void {

    const model = new RequestProject();

    if (this.selectedCustomerId) {
      model.CustomerId = this.selectedCustomerId;
    }

    model.Address = this.searchAddress || '';
    model.IsActive = this.isActive;
    model.IsDeleted = this.isDeleted;
    model.Index = 1;
    model.Limit = 100000;
    model.OrderBy = 'Number DESC';

    (model as any).startDate = this.startDate
      ? this.datePipe.transform(this.startDate, 'yyyy-MM-dd')
      : null;

    (model as any).endDate = this.endDate
      ? this.datePipe.transform(this.endDate, 'yyyy-MM-dd')
      : null;

    (model as any).FileType = fileType;

    this.showSpinner = true;

    this.customersService
      .GetProjectsFile<ResponseBase>(this.selectedCustomerId, model)
      .subscribe({
        next: (response) => {
          this.showSpinner = false;
          if (!response || !response.result) {
            return;
          }

          // // 🔹 Filename
          // const datetime = moment().format('YYYYMMDDHHmmss');
          // let fileName = 'Projects_' + datetime;

          // if (fileType === 1) {
          //   fileName += '.xlsx';
          // } else if (fileType === 2) {
          //   fileName += '.pdf';
          // }
          // 🔹 Email (if needed)
          if (emailSending) {
            emailSending.attachmentBase64Files = response.result;
            this.sendEmail(emailSending);
          }
          else {
            // 🔹 Download
            for (let i = 0; i < response.result.length; i++) {
              this.downloadBase64File(response.result[i].fileBase64, response.result[i].fileName);
            }
          }
        },
        error: () => {
          this.showSpinner = false;
        }
      });
  }


  sendEmailPopup() {

    const emailSendingParam = new EmailSendingParam();
    emailSendingParam.fromName = this.authService.getUserName();
    emailSendingParam.from = this.authService.getUserEmail();
    emailSendingParam.displayAttachement = true;
    emailSendingParam.displayExcelAttachment = true;
    emailSendingParam.to = " ";
    emailSendingParam.toName = " ";
    emailSendingParam.subject = "Materialer rapport fra Vibe malerfirma.";
    emailSendingParam.coworkerList = this.coWokers;
    emailSendingParam.isLoadDefaultEmailTemplate = true;

    const dialogRef = this.dialog.open(TextEditorEmailSendComponent, {
      height: '90%',
      width: '90%',
      data: emailSendingParam
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      const emailSending: EmailSendingParam = result;

      // 🔹 If attachment needed → generate file first
      let fileType = 0;
      if (emailSending.sendAttachement == true && emailSending.excelAttachment) {
        fileType = 3;
      }
      else if (emailSending.sendAttachement == true) {
        fileType = 2;
      }
      else if (emailSending.excelAttachment == true) {
        fileType = 1;
      }
      if (fileType > 0) {
        this.processFileAndEmail(fileType, emailSending);
      }

    });
  }

  sendEmail(emailSending) {
    this.showSpinner = true;
    this.companyInfoService.SendEmail<ResponseBase>(emailSending)
      .subscribe({
        next: (response: ResponseBase) => {
          this.showSpinner = false;
          this.translate.get("MESSAGES.SENDEMAILSUCCESS").subscribe({
            next: (res) => {
              this.notificationsService.create(res);
            },
            error: err => {
            }
          });
          this.commonService.checkEmailIsToBeSent();
        },
        error: err => {
          this.showSpinner = false;
        },
        complete: () => {
          this.showSpinner = false;
        }
      });
  }


  private downloadBase64File(base64Data: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = base64Data;
    link.download = fileName;
    link.click();
  }

  goToRequisitionTab(event) {
    var id = event.sagId;
    this._router.navigate(['/projects/edit', id], { queryParams: { isActive: this.isActive, tabIndex: 1 } });
  }
  goToRequisitionMaterialTab(event) {
    var id = event.sagId;
    this._router.navigate(['/projects/edit', id], { queryParams: { isActive: this.isActive, tabIndex: 1, reqTabIndex: 1 } });
  }

  goToRequisitionTabMode(event) {
    var id = event.project.sagId;
    if (event.tabChangeName == "Status")
      this._router.navigate(['/projects/edit', id], { queryParams: { isActive: this.isActive, tabIndex: 1, reqTabIndex: 1 } });
    if (event.tabChangeName == "Material")
      this._router.navigate(['/projects/edit', id], { queryParams: { isActive: this.isActive, tabIndex: 1, reqTabIndex: 1 } });
    if (event.tabChangeName == "Coworker")
      this._router.navigate(['/projects/edit', id], { queryParams: { isActive: this.isActive, tabIndex: 1, reqTabIndex: 2 } });
    if (event.tabChangeName == "Economy")
      this._router.navigate(['/projects/edit', id], { queryParams: { isActive: this.isActive, tabIndex: 1, reqTabIndex: 2 } });
  }

  gotoMessageLogTab(event) {
    var id = event.customerId;
    this._router.navigate(['/customers/edit', id], { queryParams: { isActive: 1, tabIndex: 1, reqTabIndex: 5 } });
  }

  EditCustomer(id) {
    this._router.navigate(['/projects/edit', id], { queryParams: { isActive: this.isActive } }); //navigationExtras
  }
}
